// ResumoPedidos.tsx
import { useEffect, useMemo, useState } from 'react';
import { API } from 'services/api';
import './selectPedido.css'

export type Pedido = {
    id: number;
    cliente_id: number;
    pedido_ref: string;
    moeda: string | null;
    total: string | number | null;
    nomeComprador?: string | null;
    emailComprador?: string | null;
    telefoneComprador?: string | null;
    endereco?: string | null;
    cidade?: string | null;
    estado?: string | null;
    CEP?: string | null;
    pais?: string | null;
    itens?: any[];
    created_at?: string;
};

function fmtMoney(total: number | string | null | undefined, moeda?: string | null) {
    if (total == null) return '—';
    const n = typeof total === 'string' ? Number(total) : total;
    if (!Number.isFinite(n)) return '—';
    try {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: moeda || 'USD' }).format(n);
    } catch {
        return `${moeda || ''} ${Number(n).toFixed(2)}`.trim();
    }
}

export default function ResumoPedidos({
    value,
    onChange,
    onSelectPedido, // ← NOVO
}: {
    value?: string;
    onChange: (v: string) => void;
    onSelectPedido?: (p: Pedido | null) => void; // ← NOVO
}) {
    const [itens, setItens] = useState<Pedido[]>([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                setLoading(true); setErr(null);
                const { data } = await API.get('/pedidos', { params: { limit: 100 } });
                const rows: Pedido[] = Array.isArray(data?.itens) ? data.itens : [];
                if (alive) setItens(rows);
            } catch (e: any) {
                if (alive) setErr(e?.response?.data?.error || e.message || 'Falha ao listar pedidos');
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const opts = useMemo(() => itens.map(p => {
        const nomesItens = Array.isArray(p.itens)
            ? p.itens.map((it: any) => it.titulo || it.title || it.sku).filter(Boolean).slice(0, 3).join(', ')
            : '';
        const rotulo =
            `${p.pedido_ref || '—'} • ` +
            `${p.nomeComprador || 'Sem nome'} • ` +
            `${[p.cidade, p.estado, p.pais].filter(Boolean).join('/')} • ` +
            `${fmtMoney(p.total, p.moeda)}` +
            `${nomesItens ? ` • Itens: ${nomesItens}` : ''}`;
        return { value: p.pedido_ref, label: rotulo };
    }), [itens]);

    const selecionado = useMemo(
        () => itens.find(p => p.pedido_ref === value) || null,
        [itens, value]
    );

    // notifica o pai quando mudar
    useEffect(() => { onSelectPedido?.(selecionado); }, [selecionado, onSelectPedido]);
    return (
        <div className='cotq'>
            {err && <div className="cotq__muted">Erro: {err}</div>}
            <select
                value={value || ''}
                onChange={e => {
                    const v = e.target.value;
                    onChange(v);
                    onSelectPedido?.(itens.find(p => p.pedido_ref === v) || null);
                }}
                className="cotq__select"
                disabled={loading}
            >
                <option value="">{loading ? 'Carregando…' : 'Selecione um pedido…'}</option>
                {opts.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>

            {selecionado && (
                <div className="cotq__panel" style={{ marginTop: 8 }}>
                    <h4 className="cotq__panel-title">Pedido {selecionado.pedido_ref}</h4>
                    <div className="cotq__grid cotq__grid--2">
                        <div><strong>Comprador:</strong> {selecionado.nomeComprador || '—'}</div>
                        <div><strong>Email:</strong> {selecionado.emailComprador || '—'}</div>
                        <div><strong>Telefone:</strong> {selecionado.telefoneComprador || '—'}</div>
                        <div><strong>Valor:</strong> {fmtMoney(selecionado.total, selecionado.moeda)}</div>
                        <div><strong>Cidade/UF:</strong> {[selecionado.cidade, selecionado.estado].filter(Boolean).join(' / ') || '—'}</div>
                        <div><strong>CEP/Pais:</strong> {[selecionado.CEP, selecionado.pais].filter(Boolean).join(' • ') || '—'}</div>
                    </div>
                    {Array.isArray(selecionado.itens) && !!selecionado.itens.length && (
                        <div style={{ marginTop: 8 }}>
                            <strong>Itens:</strong>
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {selecionado.itens.slice(0, 5).map((it, i) => (
                                    <li key={i}>{`Item ${i + 1}`} — {it.titulo || it.title || it.sku}</li>
                                ))}
                                {selecionado.itens.length > 5 && (
                                    <li>… +{selecionado.itens.length - 5} itens</li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
